import { useSaveJobButtonQuery } from "./__generated__/SaveJobButton.gqlgenerated";
import { createSaveJobAuthRedirectUrl, navigateToSaveJobAuthRedirect } from "./saveJobAuthRedirect";
import { SaveJobButtonIcon } from "./SaveJobButtonIcon";
import useSaveJob from "./useToggleSaveJob";
import { Button } from "../../../../../../mads/Button";
import { useI18n } from "../../../../../../shared/I18n";
import openLoginModal from "../../../../../../shared/native-modules/tare/openLoginModal";
import {
  dispatchSavedJobStateChanged,
  type SaveButtonSyncState,
  subscribeSavedJobStateChanged,
} from "../../../../../../shared/native-modules/tare/savedJobStateChangedEvent";
import { updateViewJobHint } from "../../../../../../shared/native-modules/tare/viewJobHint";
import eventEmitter from "../../../../../../shared/util/eventEmitter";
import { recordNpsSaveTrigger } from "../../../../../../shared/util/npsSaveTrigger";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { JsoSaveJobGateTst } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import {
  useViewJobDataContext,
  useViewjobOneGraphModel,
} from "../../../../context/ViewJobDataContext";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";
import { useIsDesktop } from "../../../../context/ViewJobScreenDisplay";
import PassportLoginModal from "../ViewJobButtonLink/PassportLoginModal";

import {
  AvroOptional,
  log as logSaveJobClick,
  SaveJobAction,
  SaveJobOriginPage,
} from "@indeed/signals-schema/com/indeed/signals/schema/jobsearch/saveJobClicks";
import React, { useCallback, useEffect, useState } from "react";
import { AccessibilityInfo, Platform } from "react-native";

export interface SaveJobButtonProps {
  jobKey: string;
  isLoggedIn: boolean;
  reload: () => void;
  variant?: "tertiary" | "utility";
}

const getSaveButtonSyncState = (
  state: string | null | undefined,
): SaveButtonSyncState | undefined =>
  state === "SAVED" || state === "VISITED" ? state : undefined;

export function SaveJobButton(props: SaveJobButtonProps): React.JSX.Element {
  const { jobKey, isLoggedIn, reload, variant = "tertiary" } = props;
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const viewJobDataContext = useViewJobDataContext();
  const viewJobDisplay = useRnViewJobDisplay();
  const saveJobGateAllocation = useViewjobProctorAllocation(JsoSaveJobGateTst);
  const { data } = useSaveJobButtonQuery({
    variables: {
      jobKey,
    },
  });
  const { toggle } = useSaveJob(jobKey);
  const i18n = useI18n();
  const isDesktop = useIsDesktop();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [externalSavedJobState, setExternalSavedJobState] = useState<SaveButtonSyncState | null>(
    null,
  );

  const job = data?.jobData?.results?.[0]?.job;
  const mobvjtk = viewjobOneGraphModel?.mobvjtk;
  const preloadedSavedJobState = getSaveButtonSyncState(viewjobOneGraphModel?.job?.savedJob?.state);
  const querySavedJobState = job?.savedJob?.state;
  const state = externalSavedJobState ?? querySavedJobState ?? preloadedSavedJobState;
  const isCurrentSaved = state === "SAVED";
  const contextualSignInPassportURL =
    viewjobOneGraphModel?.thirdPartyApplyCreateAccountModel?.contextualSignInPassportURL ?? "";
  const shouldUseFullRedirect =
    Platform.OS === "web" && saveJobGateAllocation.value === JsoSaveJobGateTst.buckets.Test;
  const saveJobAuthRedirectUrl = createSaveJobAuthRedirectUrl({
    domain: viewJobDataContext.domain,
    from: viewJobDataContext.from,
    viewjobSourceUrl: viewJobDataContext.viewjobSourceUrl,
    viewJobDisplay,
    legacyLoginUrl: viewJobDataContext.initialData?.saveJobButtonContainerModel?.myIndeedLoginLink,
  });
  useEffect(() => {
    setExternalSavedJobState(null);
  }, [jobKey, querySavedJobState, preloadedSavedJobState]);

  useEffect(() => {
    const subscription = subscribeSavedJobStateChanged((event) => {
      if (event.jobKey !== jobKey || event.source === "viewjob") {
        return;
      }

      setExternalSavedJobState(event.state);
      void updateViewJobHint(jobKey, { isSaved: event.isSaved });
    });

    return () => {
      subscription.remove();
    };
  }, [jobKey]);

  const performToggleSave = useCallback(async () => {
    const result = await toggle(state);
    setExternalSavedJobState(result.state);
    await updateViewJobHint(jobKey, { isSaved: result.isSaved });
    dispatchSavedJobStateChanged({
      eventType: "SavedJobStateChanged",
      jobKey,
      state: result.state,
      isSaved: result.isSaved,
      source: "viewjob",
    });
    if (result.isSaved) {
      eventEmitter.emit("finishSaveJob", { jobKeys: [jobKey] });
      void recordNpsSaveTrigger();
    }
    AccessibilityInfo.announceForAccessibility(
      result.isSaved ? i18n.gettext("Job saved") : i18n.gettext("Job removed"),
    );
  }, [toggle, state, jobKey, i18n]);

  const handleAuthSuccess = useCallback(async () => {
    setIsLoginModalOpen(false);
    try {
      await performToggleSave();
      reload();
    } catch (err) {
      console.error("Failed to save job after login", err);
    }
  }, [performToggleSave, reload]);

  const handleExitLoginModal = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const handlePress = useCallback(async () => {
    if (!isLoggedIn) {
      if (shouldUseFullRedirect) {
        navigateToSaveJobAuthRedirect(saveJobAuthRedirectUrl);
        return;
      }
      // Only open the in-page sign-in prompt on desktop web. Mobile web
      // surfaces a separate "save with the Indeed app" prompt via
      // BottomSaveJobButton, so we do not intercept here.
      if (Platform.OS === "web" && isDesktop) {
        setIsLoginModalOpen(true);
        return;
      }
      try {
        await openLoginModal(false, "viewjob");
        await performToggleSave();
        // refresh the logged-in state
        reload();
      } catch (err: unknown) {
        // Modal is cancelled
      }
    } else {
      if (!isCurrentSaved) {
        logSaveJobClick({
          mobtk: AvroOptional.string(mobvjtk),
          originPage: {
            SaveJobOriginPage: SaveJobOriginPage.viewjob,
          },
          action: {
            SaveJobAction: SaveJobAction.save,
          },
          jobKey: AvroOptional.string(jobKey),
        });
      }
      try {
        await performToggleSave();
      } catch (err) {
        console.error("Failed to update saved job state", err);
      }
    }
  }, [
    isLoggedIn,
    shouldUseFullRedirect,
    saveJobAuthRedirectUrl,
    isDesktop,
    reload,
    isCurrentSaved,
    performToggleSave,
    jobKey,
    mobvjtk,
  ]);

  return (
    <>
      <Button
        variant={variant}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
        onPress={handlePress}
        ariaLabel={isCurrentSaved ? i18n.gettext("Remove saved job") : i18n.gettext("Save job")}
        testID={isCurrentSaved ? "vj-unsaveJobButton" : "vj-saveJobButton"}
        iconOnly={true}
        size="md"
        iconAfter={<SaveJobButtonIcon isSaved={isCurrentSaved} />}
      />
      <PassportLoginModal
        isOpen={!shouldUseFullRedirect && isLoginModalOpen}
        onExit={handleExitLoginModal}
        onAuthSuccess={handleAuthSuccess}
        contextualSignInPassportURL={contextualSignInPassportURL}
        fromParameter="viewjob-save-intercept"
      />
    </>
  );
}
